import { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  ChartLine,
  Feather,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageCircle,
  Moon,
  Palette,
  PenLine,
  Settings,
  ShieldCheck,
  Sun,
  UserRound,
  X,
} from 'lucide-react';
import logoMark from '@/images/logos/logo-mark.svg';
import { LITERARY_CATEGORIES } from '@shared/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSession } from '@/lib/session';
import { useTheme } from '@/lib/theme';
import { cn, initials } from '@/lib/utils';
import { categoryLabel, locale, t } from '@/lib/i18n';
import { LanguageToggle } from '@/components/LanguageToggle';

/**
 * One header serves two very different sites.
 *
 * On a tenant host it is the author's own masthead — their pen name, their
 * sections, no platform navigation. On the platform host it is the marketplace
 * header with the feed, discovery and the writer's workspace.
 */
export function Header() {
  const { viewer, site, signOut } = useSession();
  const { theme, cycleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const tenant = site?.tenant ?? null;
  const home = '/';

  /*
   * One set of sections, one query parameter.
   *
   * These used to be `?type=poem|story|essay` — the parameter the literary
   * taxonomy replaced — so every one of them was a dead link that changed the
   * URL and filtered nothing. They also carried *form* names while the front
   * page listed *categories*, which read as two competing sets of filters.
   */
  const links = tenant
    ? [
        { to: '/', label: t('सबै रचना') },
        ...LITERARY_CATEGORIES.map((value) => ({
          to: `/?category=${value}`,
          label: categoryLabel(value),
        })),
        { to: '/about', label: t('परिचय') },
      ]
    : LITERARY_CATEGORIES.map((value) => ({
        to: `/?category=${value}`,
        label: categoryLabel(value),
      }));

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  /*
   * On the platform the masthead belongs to the front page, so the header is a
   * thin utility strip above it — the "Today's paper / Subscribe" rule that runs
   * over a real broadsheet. It only shows the wordmark once you have navigated
   * away from the front page, where the masthead is no longer on screen.
   */
  const isFrontPage = location.pathname === '/';
  const newspaper = !tenant;

  /** A section link is current when its category matches the live query. */
  const activeCategory = new URLSearchParams(location.search).get('category');
  const isSectionActive = (to: string) => {
    const target = new URL(to, 'https://x').searchParams.get('category');
    if (!target) return isFrontPage && !activeCategory;
    return isFrontPage && target === activeCategory;
  };

  // The rail carries the issue line on the front page, where the masthead below
  // it is the nameplate and repeating the name would be redundant.
  const issue = new Intl.DateTimeFormat(locale(), { month: 'long', year: 'numeric' }).format(new Date());

  return (
    <header
      className={cn(
        'sticky top-0 z-40 border-b bg-background/90 backdrop-blur',
        newspaper && 'border-foreground/80',
      )}
    >
      <div
        className={cn(
          'flex items-center gap-4',
          newspaper ? 'broadsheet h-11' : 'container h-16',
        )}
      >
        {/*
          The name belongs to the page, not the rail, whenever the page carries a
          nameplate — which the front page always does, on the platform and on a
          writer'"'"'s own site alike. Only the platform had this rule, so a writer'"'"'s
          site showed their name in the header and again as the heading directly
          beneath it.
        */}
        {!isFrontPage && (
          <Link to={home} className="flex items-center gap-2 font-semibold">
            {newspaper ? (
              <>
                <img src={logoMark} alt="" className="h-6 w-auto" />
                <span className="headline text-xl leading-none">
                  {site?.siteName ?? t('कलम प्रेस')}
                </span>
              </>
            ) : (
              <>
                {/*
                  A writer's mark is their own — their photo, or their initial —
                  never a stock quill. Every writer's site carried the same icon,
                  which is the opposite of what attaching your own domain is for.
                */}
                {tenant ? (
                  <Avatar className="size-6">
                    {tenant.avatarUrl ? <AvatarImage src={tenant.avatarUrl} alt="" /> : null}
                    <AvatarFallback className="bg-primary text-[0.6rem] text-primary-foreground">
                      {initials(tenant.penName || tenant.displayName)}
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <Feather className="size-5 text-primary" />
                )}
                <span className="np text-lg leading-none">
                  {tenant ? tenant.penName || tenant.displayName : (site?.siteName ?? t('कलम प्रेस'))}
                </span>
              </>
            )}
          </Link>
        )}

        {newspaper && isFrontPage && (
          <span className="np hidden text-[0.78rem] text-muted-foreground sm:block">{issue}</span>
        )}

        {/*
          On the front page the section strip sits directly below the masthead,
          so repeating the same links in the rail is the duplication that made
          the page look like it had two different filter sets. Everywhere else
          the strip is off-screen and the rail is the only way back to a section.

          Devanagari nav at 0.875rem and no tracking: the letterspaced small-caps
          treatment a print rail would use is Latin-only — see .kicker.
        */}
        <nav
          className={cn(
            'hidden items-center md:flex',
            newspaper ? 'gap-5' : 'ms-4 gap-1',
            newspaper && isFrontPage && 'md:hidden',
          )}
        >
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/'}
              className={cn(
                'np text-muted-foreground transition-colors hover:text-foreground',
                newspaper ? 'text-sm' : 'rounded-md px-3 py-1.5 text-sm hover:bg-accent',
                // NavLink's own isActive compares pathnames and ignores the
                // query, so every category link would light up at once. The
                // section *is* the query here, so it has to be read directly.
                isSectionActive(link.to) && 'font-medium text-foreground',
              )}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="ms-auto flex items-center gap-2">
          <LanguageToggle />
          <Button
            variant="ghost"
            size="icon"
            onClick={cycleTheme}
            title={t('रङ्ग: {theme}', { theme })}
            aria-label={t('पढ्ने रङ्ग बदल्नुहोस्')}
          >
            {theme === 'dark' ? <Moon /> : <Sun />}
          </Button>

          {viewer ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" aria-label={t('खाता')}>
                  <Avatar className="size-8">
                    {viewer.avatarUrl ? <AvatarImage src={viewer.avatarUrl} alt={viewer.displayName} /> : null}
                    <AvatarFallback>{initials(viewer.penName || viewer.displayName)}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="np">
                  {viewer.penName || viewer.displayName}
                  <div className="text-xs font-normal text-muted-foreground">@{viewer.handle}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                  <LayoutDashboard />{t('लेखन कक्ष')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/new')}>
                  <PenLine />{t('नयाँ रचना')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/comments')}>
                  <MessageCircle />{t('प्रतिक्रिया')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/analytics')}>
                  <ChartLine />{t('तथ्यांक')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/profile')}>
                  <UserRound />{t('प्रोफाइल')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/theme')}>
                  <Palette />{t('थिम')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/domain')}>
                  <Settings />{t('डोमेन सेटिङ')}</DropdownMenuItem>

                {viewer.role === 'admin' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/admin')}>
                      <ShieldCheck />{t('प्रशासन')}</DropdownMenuItem>
                  </>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut />{t('बाहिरिनुहोस्')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden items-center gap-2 sm:flex">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/login">{t('लगइन')}</Link>
              </Button>
              <Button size="sm" asChild>
                <Link to="/register">{t('लेख्न सुरु गर्नुहोस्')}</Link>
              </Button>
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label={t('मेनु')}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="container flex flex-col gap-1 border-t py-3 md:hidden">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-2 text-sm hover:bg-accent"
            >
              {link.label}
            </Link>
          ))}
          {!viewer && (
            <div className="mt-2 flex gap-2">
              <Button variant="outline" size="sm" asChild className="flex-1">
                <Link to="/login" onClick={() => setMobileOpen(false)}>{t('लगइन')}</Link>
              </Button>
              <Button size="sm" asChild className="flex-1">
                <Link to="/register" onClick={() => setMobileOpen(false)}>{t('दर्ता')}</Link>
              </Button>
            </div>
          )}
        </nav>
      )}
    </header>
  );
}
